import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  ChevronRight, ChevronDown, TrendingUp,
  AlertTriangle, Info, AlertCircle, BarChart3, FileText,
  Layers, Activity, Shield, BookOpen, Download, PieChart,
  ArrowLeftRight, MessageSquare, Loader2, ExternalLink, Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DateFilter from "@/components/DateFilter";
import { fmt, fmtShort } from "@/lib/mis-utils";
import { MISCharts } from "@/components/MISCharts";
import { MISComparison } from "@/components/MISComparison";
import { MISCommentary } from "@/components/MISCommentary";
import { AICommentary } from "@/components/AICommentary";
import MISSchedulePicker from "@/components/MISSchedulePicker";
import MISTieOutPanel from "@/components/MISTieOutPanel";
import BSDrillDialog from "@/components/BSDrillDialog";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Clickable amount ── */
function Amt({ amount, onClick, bold, muted }) {
  const isNeg = amount < 0;
  const base = muted ? "text-zinc-500" : isNeg ? "text-rose-600" : "text-zinc-900";
  return (
    <span onClick={onClick}
      className={`font-mono tabular-nums text-[13px] ${base} ${bold ? "font-bold" : "font-medium"} ${onClick ? "clickable-amount" : ""}`}
      role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      {isNeg && "-"}{fmt(amount)}
    </span>
  );
}

/* ── Inline party breakdown component ── */
function InlineBreakdown({ subhead, ledger, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = { mis_subhead: subhead, ...filters };
        if (ledger) params.ledger = ledger;
        const res = await axios.get(`${API}/mis/drilldown`, { params });
        setData(res.data);
      } catch (err) {
        logger.error("Failed to load MIS drilldown:", err);
      }
      finally { setLoading(false); }
    };
    fetch();
  }, [subhead, ledger, filters]);

  if (loading) return (
    <tr className="drill-row"><td colSpan={2} className="py-4 text-center">
      <Loader2 className="w-4 h-4 animate-spin inline-block text-zinc-400" />
    </td></tr>
  );
  if (!data?.parties?.length) return (
    <tr className="drill-row"><td colSpan={2} className="py-3 pl-14 text-xs text-zinc-400">No party data found.</td></tr>
  );

  const months = data.months || [];
  const ml = (m) => {
    if (!m) return "";
    const mo = parseInt(m.split("-")[1]);
    return ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][mo] || m;
  };

  return (
    <>
      <tr className="drill-row">
        <td colSpan={2} className="p-0">
          <div className="pl-10 pr-4 py-3 bg-zinc-50 border-l-2 border-blue-600">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                Party-wise Monthly Schedule &middot; {data.party_count} parties &middot; {fmtShort(data.total)}
              </p>
              <button
                onClick={async () => {
                  try {
                    const params = new URLSearchParams({ mis_subhead: subhead, ...filters });
                    if (ledger) params.set("ledger", ledger);
                    const res = await axios.get(`${API}/mis/export/schedule?${params.toString()}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a"); a.href = url;
                    a.download = `Schedule_${(subhead || "").replace(/\s+/g, "_")}.xlsx`;
                    a.click(); window.URL.revokeObjectURL(url);
                  } catch (err) {
                    logger.error("Schedule export failed:", err);
                    toast.error("Failed to download schedule");
                  }
                }}
                className="flex items-center gap-1 text-[10px] font-bold text-zinc-500 hover:text-zinc-900 bg-white border border-zinc-200 rounded px-2 py-1 hover:border-zinc-400 transition-colors"
                data-testid="download-schedule-btn"
              >
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-zinc-300">
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 py-2 pr-3 sticky left-0 bg-zinc-50 min-w-[160px]">Particulars</th>
                    {months.map((m) => (
                      <th key={m} className="text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500 py-2 px-2 min-w-[80px]">{ml(m)}</th>
                    ))}
                    <th className="text-right text-[10px] font-bold uppercase tracking-wider text-zinc-900 py-2 pl-2 min-w-[95px] bg-zinc-100/50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.parties.slice(0, 30).map((p) => (
                    <tr key={p.party} className="border-b border-zinc-100 hover:bg-white transition-colors">
                      <td className="text-[12px] text-zinc-800 py-1.5 pr-3 font-medium sticky left-0 bg-zinc-50">{p.party}</td>
                      {months.map((m) => {
                        const val = p.monthly?.[m] || 0;
                        return (
                          <td key={m} className="text-right font-mono tabular-nums text-[11px] text-zinc-600 py-1.5 px-2">
                            {val !== 0 ? fmt(val) : <span className="text-zinc-300">-</span>}
                          </td>
                        );
                      })}
                      <td className="text-right font-mono tabular-nums text-[12px] text-zinc-900 font-bold py-1.5 pl-2 bg-zinc-100/30">{fmt(p.total)}</td>
                    </tr>
                  ))}
                  {data.parties.length > 30 && (
                    <tr><td colSpan={months.length + 2} className="text-[11px] text-zinc-400 py-1.5">...and {data.parties.length - 30} more parties</td></tr>
                  )}
                  {/* Total row */}
                  <tr className="border-t-2 border-zinc-300 bg-zinc-100/50">
                    <td className="text-[12px] font-bold text-zinc-900 py-2 pr-3 sticky left-0 bg-zinc-100/50">Total</td>
                    {months.map((m) => (
                      <td key={m} className="text-right font-mono tabular-nums text-[12px] font-bold text-zinc-900 py-2 px-2">
                        {data.monthly_totals?.[m] ? fmt(data.monthly_totals[m]) : <span className="text-zinc-300">-</span>}
                      </td>
                    ))}
                    <td className="text-right font-mono tabular-nums text-[12px] font-black text-zinc-950 py-2 pl-2">{fmt(data.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

const TABS = [
  { id: "summary", label: "Overview", icon: BarChart3 },
  { id: "ai", label: "AI Insights", icon: Sparkles },
  { id: "charts", label: "Charts", icon: PieChart },
  { id: "pl", label: "P&L", icon: TrendingUp },
  { id: "monthly", label: "Monthly", icon: FileText },
  { id: "bs", label: "Balance Sheet", icon: Layers },
  { id: "wc", label: "Working Capital", icon: Activity },
  { id: "ratios", label: "Ratios", icon: BarChart3 },
  { id: "redflags", label: "Red Flags", icon: AlertTriangle },
  { id: "comparison", label: "Comparison", icon: ArrowLeftRight },
  { id: "commentary", label: "Commentary", icon: MessageSquare },
  { id: "assumptions", label: "Notes", icon: BookOpen },
];

/* ═══════════════════════════════════════
   EXECUTIVE SUMMARY / OVERVIEW
   ═══════════════════════════════════════ */
function SummaryTab({ data, companyContext, filters, onOpenAI }) {
  const pl = data.pl;
  const r = data.ratios;

  const KPI = ({ label, value, sub }) => (
    <div className="bg-white rounded-lg border border-zinc-200 p-5 card-hover">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">{label}</p>
      <p className={`text-2xl font-black mt-2 tracking-tight ${value < 0 ? "text-rose-600" : "text-zinc-950"}`}
        style={{ fontFamily: "'Chivo', system-ui" }}>{fmtShort(value)}</p>
      {sub && <p className="text-[11px] text-zinc-400 mt-1 font-mono">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="mis-summary-tab">
      {/* AI teaser (only visible when ≥2 months in window) */}
      <AICommentary
        companyContext={companyContext}
        filters={filters}
        compact
        onOpenFull={onOpenAI}
      />

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-4">
        <KPI label="Net Revenue" value={pl.total_revenue} sub={`${r.gross_margin_pct}% gross margin`} />
        <KPI label="Gross Profit" value={pl.gross_profit} />
        <KPI label="EBITDA" value={pl.ebitda} sub={`${r.ebitda_margin_pct}% margin`} />
        <KPI label="Profit Before Tax" value={pl.pbt} sub={`${r.net_margin_pct}% net margin`} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* P&L Waterfall - 2 cols */}
        <div className="col-span-2 bg-white rounded-lg border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h3 className="text-sm font-bold text-zinc-900" style={{ fontFamily: "'Chivo'" }}>Income Statement Flow</h3>
          </div>
          <div className="px-5 py-4">
            {[
              { l: "Revenue", v: pl.total_revenue, i: 0 },
              { l: "(-) Cost of Materials", v: -pl.cogs, i: 1 },
              { l: "(-) Other Direct Costs", v: -pl.other_direct_costs, i: 1 },
              { l: "Gross Profit", v: pl.gross_profit, i: 0, b: true },
              { l: "(-) Employee Costs", v: -pl.total_employee_costs, i: 1 },
              { l: "(-) Operating Overheads", v: -pl.total_overheads, i: 1 },
              { l: "EBITDA", v: pl.ebitda, i: 0, b: true },
              { l: "(-) Depreciation", v: -pl.total_depreciation, i: 1 },
              { l: "(-) Finance Costs", v: -pl.total_finance_costs, i: 1 },
              { l: "Profit Before Tax", v: pl.pbt, i: 0, b: true },
            ].map((row) => (
              <div key={row.l}
                className={`flex justify-between items-center py-2 ${row.b ? 'border-t border-zinc-200 mt-1 pt-3' : ''}`}
                style={{ paddingLeft: row.i * 24 }}>
                <span className={`text-[13px] ${row.b ? 'font-bold text-zinc-950' : 'text-zinc-500'}`}>{row.l}</span>
                <Amt amount={row.v} bold={row.b} />
              </div>
            ))}
          </div>
        </div>

        {/* Ratios panel */}
        <div className="bg-white rounded-lg border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h3 className="text-sm font-bold text-zinc-900" style={{ fontFamily: "'Chivo'" }}>Key Metrics</h3>
          </div>
          <div className="px-5 py-4 space-y-2.5">
            {[
              { l: "Gross Margin", v: `${r.gross_margin_pct}%` },
              { l: "EBITDA Margin", v: `${r.ebitda_margin_pct}%` },
              { l: "Net Margin", v: `${r.net_margin_pct}%` },
              { sep: true },
              { l: "Employee / Rev", v: `${r.employee_cost_to_revenue_pct}%` },
              { l: "Overheads / Rev", v: `${r.overheads_to_revenue_pct}%` },
              { l: "Finance / Rev", v: `${r.finance_cost_to_revenue_pct}%` },
              { sep: true },
              { l: "Current Ratio", v: `${r.current_ratio}x` },
              { l: "Debtor Days", v: `${r.debtor_days}` },
              { l: "Creditor Days", v: `${r.creditor_days}` },
              { l: "WC Cycle", v: `${r.working_capital_cycle_days}d` },
            ].map((row, idx) => row.sep ? (
              <div key={`sep-${idx}`} className="border-t border-zinc-100 my-1" />
            ) : (
              <div key={row.l} className="flex justify-between items-center">
                <span className="text-[12px] text-zinc-500">{row.l}</span>
                <span className="text-[12px] font-mono font-bold text-zinc-900">{row.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {data.red_flags.length > 0 && (
        <div className="bg-amber-50 border border-amber-200/60 rounded-lg px-5 py-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {data.red_flags.length} Alert(s)
          </p>
          {data.red_flags.slice(0, 3).map((rf) => (
            <p key={rf.title} className="text-[12px] text-amber-800 py-0.5">
              <strong>{rf.title}</strong> — {rf.detail.slice(0, 120)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   P&L WITH INLINE DRILL-DOWN
   ═══════════════════════════════════════ */
function PLSection({ section, drillState, onToggleDrill, filters }) {
  if (!section?.items?.length) return null;

  return (
    <>
      {section.items.map((item) => {
        const key = item.subhead;
        const isLedgersOpen = drillState[`expand_${key}`];
        const isDrillOpen = drillState[`drill_${key}`];
        const hasLedgers = item.ledgers?.length > 0;

        return (
          <React.Fragment key={key}>
            {/* Subhead row */}
            <TableRow className="hover:bg-zinc-50/60 transition-colors"
              data-testid={`mis-pl-row-${key.replace(/\s+/g, "-").toLowerCase()}`}>
              <TableCell className="text-[13px]" style={{ paddingLeft: 20 }}>
                <div className="flex items-center gap-2">
                  <button className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-zinc-200 transition-colors"
                    onClick={() => hasLedgers && onToggleDrill(`expand_${key}`)}>
                    {hasLedgers && (isLedgersOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />)}
                  </button>
                  <span className="font-medium text-zinc-800">{key}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Amt amount={item.total} onClick={() => onToggleDrill(`drill_${key}`)} />
              </TableCell>
            </TableRow>

            {/* Inline party breakdown for subhead */}
            {isDrillOpen && (
              <InlineBreakdown subhead={key} filters={filters} />
            )}

            {/* Expanded ledger rows */}
            {isLedgersOpen && item.ledgers.map((lg) => {
              const lgDrillKey = `drill_${key}_${lg.name}`;
              const isLgDrillOpen = drillState[lgDrillKey];
              return (
                <React.Fragment key={lg.name}>
                  <TableRow className="hover:bg-zinc-50/40">
                    <TableCell className="text-[12px] text-zinc-500" style={{ paddingLeft: 52 }}>
                      {lg.name}
                    </TableCell>
                    <TableCell className="text-right">
                      <Amt amount={lg.total} muted onClick={() => onToggleDrill(lgDrillKey)} />
                    </TableCell>
                  </TableRow>
                  {isLgDrillOpen && (
                    <InlineBreakdown subhead={key} ledger={lg.name} filters={filters} />
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}

function PLTab({ data, filters }) {
  const pl = data.pl;
  const [drillState, setDrillState] = useState({});
  const toggle = (key) => setDrillState(p => ({ ...p, [key]: !p[key] }));

  const directCostsFiltered = pl.direct_costs ? {
    ...pl.direct_costs,
    items: (pl.direct_costs.items || []).filter(i => i.subhead !== "Material / Purchase Cost"),
    total: (pl.direct_costs.items || []).filter(i => i.subhead !== "Material / Purchase Cost").reduce((s, i) => s + i.total, 0),
  } : null;

  const Section = ({ label, section, accent }) => {
    if (!section?.items?.length) return null;
    return (
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className={`px-5 py-2.5 border-b border-zinc-100 flex justify-between items-center ${accent || ""}`}>
          <h3 className="text-[13px] font-bold text-zinc-800" style={{ fontFamily: "'Chivo'" }}>{label}</h3>
          <span className="font-mono tabular-nums text-[13px] font-bold text-zinc-900">{fmt(section.total)}</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50/40">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">Particulars</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 text-right w-40">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <PLSection section={section} drillState={drillState} onToggleDrill={toggle} filters={filters} />
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="mis-pl-tab">
      {/* Reconciliation note — explains why MIS Indirect Expenses is split across heads */}
      <div className="rounded-md border border-blue-200 bg-blue-50/50 px-4 py-3 text-[12px] text-blue-900 flex gap-3">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>How MIS reclassifies Tally's groups:</strong>
          {" "}Tally's <em>"Indirect Expenses"</em> bucket is split into <strong>D. Employee Costs</strong>{" "}
          (salaries, wages), <strong>E. Operating Overheads</strong> (rent, telecom, repairs),{" "}
          <strong>F. Finance Costs</strong> (interest, bank charges) and <strong>G. Depreciation</strong>.
          PBT here always equals Net Profit on Statements → P&L. <em>Click any amount to drill down.</em>
        </div>
      </div>

      <Section label="A. Revenue" section={pl.revenue} accent="bg-emerald-50/20" />

      {/* COGS */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="px-5 py-2.5 border-b border-zinc-100">
          <h3 className="text-[13px] font-bold text-zinc-800" style={{ fontFamily: "'Chivo'" }}>B. Cost of Goods Sold</h3>
        </div>
        <div className="px-5 py-3 space-y-1.5">
          {[
            { l: "Opening Stock", v: pl.opening_stock },
            { l: "Add: Material / Purchase Cost", v: pl.material_cost, drill: "Material / Purchase Cost" },
            { l: "Less: Closing Stock", v: -pl.closing_stock },
            { l: "Cost of Materials Consumed", v: pl.cogs, b: true },
          ].map((row) => (
            <div key={row.l} className={`flex justify-between items-center py-1 ${row.b ? 'border-t border-zinc-200 pt-2 mt-1' : ''}`}>
              <span className={`text-[13px] ${row.b ? 'font-bold text-zinc-900' : 'text-zinc-600'}`}>{row.l}</span>
              <Amt amount={row.v} bold={row.b}
                onClick={row.drill ? () => toggle(`drill_cogs_${row.drill}`) : undefined} />
            </div>
          ))}
          {/* Inline drill for Material cost */}
          {drillState["drill_cogs_Material / Purchase Cost"] && (
            <div className="bg-zinc-50 border-l-2 border-blue-600 rounded-r pl-6 pr-3 py-2 mt-2 drill-row">
              <InlineBreakdownCompact subhead="Material / Purchase Cost" filters={filters} />
            </div>
          )}
          {pl.closing_stock === 0 && pl.opening_stock > 0 && (
            <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3" /> Closing stock not entered
            </p>
          )}
        </div>
      </div>

      <Section label="C. Other Direct Costs" section={directCostsFiltered} />
      <Section label="D. Employee Costs" section={pl.employee_costs} />
      <Section label="E. Operating Overheads" section={pl.operating_overheads} />
      <Section label="F. Finance Costs" section={pl.finance_costs} />
      <Section label="G. Depreciation" section={pl.depreciation} />

      {/* Bottom totals */}
      <div className="bg-zinc-950 text-white rounded-lg px-5 py-4">
        {[
          { l: "Gross Profit", v: pl.gross_profit },
          { l: "EBITDA", v: pl.ebitda },
          { l: "EBIT", v: pl.ebit },
          { l: "Profit Before Tax", v: pl.pbt },
        ].map((row) => (
          <div key={row.l} className="flex justify-between items-center py-1.5">
            <span className="text-[13px] font-semibold text-zinc-300">{row.l}</span>
            <span className={`font-mono tabular-nums text-[13px] font-bold ${row.v < 0 ? "text-rose-400" : "text-emerald-400"}`}>
              {row.v < 0 && "-"}{fmt(row.v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Compact inline breakdown for COGS area */
function InlineBreakdownCompact({ subhead, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const f = async () => {
      setLoading(true);
      try {
        const params = { mis_subhead: subhead, ...filters };
        const res = await axios.get(`${API}/mis/drilldown`, { params });
        setData(res.data);
      } catch (err) {
        logger.error("MISReport subhead drilldown failed:", err);
      }
      finally { setLoading(false); }
    };
    f();
  }, [subhead, filters]);

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />;
  if (!data?.parties?.length) return <p className="text-[11px] text-zinc-400">No data.</p>;

  const months = data.months || [];
  const ml = (m) => { const mo = parseInt(m.split("-")[1]); return ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][mo] || m; };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[500px]">
        <thead>
          <tr className="border-b border-zinc-200">
            <th className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400 py-1.5 pr-3 min-w-[140px]">Particulars</th>
            {months.map((m) => <th key={m} className="text-right text-[10px] font-bold uppercase tracking-wider text-zinc-400 py-1.5 px-1.5 min-w-[70px]">{ml(m)}</th>)}
            <th className="text-right text-[10px] font-bold uppercase tracking-wider text-zinc-900 py-1.5 pl-1.5 min-w-[80px]">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.parties.slice(0, 15).map((p) => (
            <tr key={p.party} className="border-b border-zinc-100 last:border-0">
              <td className="text-[11px] text-zinc-700 py-1 font-medium pr-3">{p.party}</td>
              {months.map((m) => {
                const val = p.monthly?.[m] || 0;
                return <td key={m} className="text-right font-mono tabular-nums text-[10px] text-zinc-600 py-1 px-1.5">{val !== 0 ? fmt(val) : <span className="text-zinc-300">-</span>}</td>;
              })}
              <td className="text-right font-mono tabular-nums text-[11px] text-zinc-900 py-1 font-semibold pl-1.5">{fmt(p.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════
   MONTHLY VIEW
   ═══════════════════════════════════════ */
function MonthlyTab({ data }) {
  const ms = data.monthly_summary || [];
  if (!ms.length) return <p className="text-sm text-zinc-400">No monthly data.</p>;

  const ml = (m) => { const mo = parseInt(m.split("-")[1]); return ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][mo] + " " + m.slice(2,4); };
  const rows = [
    { l: "Revenue", k: "revenue" }, { l: "Direct Costs", k: "direct_costs" },
    { l: "Stock Adjustment", k: "stock_adjustment", muted: true },
    { l: "Gross Profit", k: "gross_profit", b: true },
    { l: "Employee Costs", k: "employee_costs" }, { l: "Overheads", k: "overheads" },
    { l: "EBITDA", k: "ebitda", b: true },
    { l: "Finance Costs", k: "finance_costs" }, { l: "Depreciation", k: "depreciation" },
    { l: "PBT", k: "pbt", b: true },
  ];
  const ytd = {};
  rows.forEach(r => { ytd[r.k] = ms.reduce((sum, m) => sum + (m[r.k] || 0), 0); });

  return (
    <div data-testid="mis-monthly-tab" className="bg-white rounded-lg border border-zinc-200 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 sticky left-0 bg-zinc-50 min-w-[160px]">Particulars</TableHead>
            {ms.map((m) => <TableHead key={m.month} className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 text-right min-w-[100px]">{ml(m.month)}</TableHead>)}
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-900 text-right min-w-[100px] bg-zinc-100">YTD</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.k} className={row.b ? "bg-zinc-50/50 border-t border-zinc-200" : ""}>
              <TableCell className={`text-[13px] sticky left-0 bg-white ${row.b ? "font-bold text-zinc-900 bg-zinc-50/50" : "text-zinc-600"}`}>{row.l}</TableCell>
              {ms.map((m) => <TableCell key={m.month} className="text-right"><Amt amount={m[row.k]} bold={row.b} /></TableCell>)}
              <TableCell className="text-right bg-zinc-50"><Amt amount={ytd[row.k]} bold /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ═══════════════════════════════════════
   BALANCE SHEET WITH INLINE DRILL
   ═══════════════════════════════════════ */
function BSTab({ data, filters }) {
  const bs = data.balance_sheet;
  const [expanded, setExpanded] = useState({});
  const [drill, setDrill] = useState({ open: false, name: "", side: "asset" });

  const openDrill = (name, side) => setDrill({ open: true, name, side });
  const closeDrill = () => setDrill((d) => ({ ...d, open: false }));

  const Side = ({ items, label, total, sideKey }) => (
    <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
      <div className="px-5 py-2.5 border-b border-zinc-100 flex justify-between">
        <h3 className="text-[13px] font-bold text-zinc-800" style={{ fontFamily: "'Chivo'" }}>{label}</h3>
        <span className="font-mono tabular-nums text-[13px] font-bold text-zinc-900">{fmt(total)}</span>
      </div>
      <Table>
        <TableHeader><TableRow className="bg-zinc-50/40">
          <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">Head</TableHead>
          <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 text-right w-36">Amount</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((item) => {
            const isExp = expanded[item.name];
            const has = item.children?.length > 0;
            const drillable = !item.is_profit && !item.is_manual;
            return (
              <React.Fragment key={`${label}-${item.name}`}>
                <TableRow className="hover:bg-zinc-50/60 transition-colors">
                  <TableCell
                    className="text-[13px] font-medium text-zinc-800 cursor-pointer"
                    onClick={() => has && setExpanded(p => ({ ...p, [item.name]: !p[item.name] }))}
                  >
                    <div className="flex items-center gap-2">
                      {has ? (isExp ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />) : <span className="w-3.5" />}
                      <span>{item.name}</span>
                      {drillable && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openDrill(item.name, sideKey); }}
                          className="p-0.5 rounded hover:bg-zinc-200 text-zinc-300 hover:text-blue-600 transition"
                          title="Party-wise drill-down"
                          data-testid={`mis-bs-drill-${item.name?.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-right cursor-pointer"
                    onClick={() => has && setExpanded(p => ({ ...p, [item.name]: !p[item.name] }))}
                  >
                    <Amt amount={item.amount} />
                  </TableCell>
                </TableRow>
                {isExp && item.children.map((sub, idx) => (
                  <TableRow key={`${sub.name}-${idx}`} className="hover:bg-zinc-50/40">
                    <TableCell className="text-[12px] text-zinc-500" style={{ paddingLeft: 40 }}>{sub.name}</TableCell>
                    <TableCell className="text-right"><Amt amount={sub.amount} muted /></TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mis-bs-tab">
      <Side items={bs.assets} label="Assets" total={bs.total_assets} sideKey="asset" />
      <Side items={bs.liabilities} label="Liabilities" total={bs.total_liabilities} sideKey="liability" />
      <BSDrillDialog
        open={drill.open}
        onClose={closeDrill}
        groupName={drill.name}
        companyId={filters?.company_id || data?.period?.company_id || "default"}
        branchId={filters?.branch_id}
        fromDate={filters?.from_date || data?.period?.from_date}
        toDate={filters?.to_date || data?.period?.to_date}
        sideColor={drill.side}
      />
    </div>
  );
}

/* ═══════ Remaining tabs — compact ═══════ */
function WCTab({ data }) {
  const wc = data.working_capital; const r = data.ratios;
  const Card = ({ title, items }) => (
    <div className="bg-white rounded-lg border border-zinc-200">
      <div className="px-5 py-2.5 border-b border-zinc-100"><h3 className="text-[13px] font-bold text-zinc-900" style={{ fontFamily: "'Chivo'" }}>{title}</h3></div>
      <div className="px-5 py-3 space-y-2">
        {items.map((row) => (
          <div key={row.l} className={`flex justify-between items-center py-1 ${row.b ? "border-t border-zinc-200 pt-2" : ""}`}>
            <span className={`text-[13px] ${row.b ? "font-bold text-zinc-900" : "text-zinc-500"}`}>{row.l}</span>
            {row.v !== undefined ? <Amt amount={row.v} bold={row.b} /> : <span className="text-[13px] font-mono font-bold text-zinc-900">{row.s}</span>}
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="mis-wc-tab">
      <Card title="Working Capital" items={[
        { l: "Receivables", v: wc.receivables }, { l: "Inventory", v: wc.inventory },
        { l: "Gross Current Assets", v: wc.gross_current_assets, b: true },
        { l: "Less: Payables", v: wc.payables }, { l: "Less: Statutory Dues", v: wc.statutory_dues },
        { l: "Net Working Capital", v: wc.net_working_capital, b: true },
      ]} />
      <Card title="Cash Cycle" items={[
        { l: "Debtor Days", s: `${r.debtor_days} days` }, { l: "Inventory Days", s: `${r.inventory_days} days` },
        { l: "Creditor Days", s: `${r.creditor_days} days` }, { l: "WC Cycle", s: `${r.working_capital_cycle_days} days`, b: true },
      ]} />
    </div>
  );
}

function RatiosTab({ data }) {
  const r = data.ratios;
  const groups = [
    { t: "Profitability", items: [{ l: "Gross Margin", v: r.gross_margin_pct, s: "%" }, { l: "EBITDA Margin", v: r.ebitda_margin_pct, s: "%" }, { l: "Net Margin", v: r.net_margin_pct, s: "%" }] },
    { t: "Cost Structure", items: [{ l: "Employee / Revenue", v: r.employee_cost_to_revenue_pct, s: "%" }, { l: "Overheads / Revenue", v: r.overheads_to_revenue_pct, s: "%" }, { l: "Finance / Revenue", v: r.finance_cost_to_revenue_pct, s: "%" }] },
    { t: "Efficiency", items: [{ l: "Debtor Days", v: r.debtor_days, s: "d" }, { l: "Creditor Days", v: r.creditor_days, s: "d" }, { l: "Inventory Days", v: r.inventory_days, s: "d" }, { l: "WC Cycle", v: r.working_capital_cycle_days, s: "d" }] },
    { t: "Liquidity", items: [{ l: "Current Ratio", v: r.current_ratio, s: "x" }, { l: "Quick Ratio", v: r.quick_ratio, s: "x" }] },
  ];
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="mis-ratios-tab">
      {groups.map((g) => (
        <div key={g.t} className="bg-white rounded-lg border border-zinc-200">
          <div className="px-5 py-2.5 border-b border-zinc-100"><h3 className="text-[13px] font-bold text-zinc-900" style={{ fontFamily: "'Chivo'" }}>{g.t}</h3></div>
          <div className="px-5 py-3 space-y-2">
            {g.items.map((i) => (
              <div key={i.l} className="flex justify-between"><span className="text-[12px] text-zinc-500">{i.l}</span><span className={`text-[12px] font-mono font-bold ${i.v < 0 ? "text-rose-600" : "text-zinc-900"}`}>{i.v}{i.s}</span></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RedFlagsTab({ data }) {
  const flags = data.red_flags || [];
  if (!flags.length) return <div className="bg-white border border-emerald-200 rounded-lg p-8 text-center" data-testid="mis-redflags-tab"><Shield className="w-8 h-8 text-emerald-500 mx-auto mb-2" /><p className="text-sm font-bold text-emerald-700">No red flags</p></div>;
  const icons = { alert: AlertCircle, warning: AlertTriangle, info: Info };
  const colors = { alert: "border-rose-200 bg-rose-50/30", warning: "border-amber-200 bg-amber-50/30", info: "border-zinc-200 bg-zinc-50" };
  return (
    <div className="space-y-3" data-testid="mis-redflags-tab">
      {flags.map((rf) => { const Icon = icons[rf.type] || Info; return (
        <div key={rf.title} className={`border rounded-lg p-4 flex items-start gap-3 ${colors[rf.type]}`}>
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-zinc-600" />
          <div><p className="text-[13px] font-bold text-zinc-800">{rf.title}</p><p className="text-[12px] text-zinc-600 mt-0.5">{rf.detail}</p></div>
        </div>
      ); })}
    </div>
  );
}

function AssumptionsTab({ data }) {
  return (
    <div className="bg-white rounded-lg border border-zinc-200" data-testid="mis-assumptions-tab">
      <div className="px-5 py-2.5 border-b border-zinc-100"><h3 className="text-[13px] font-bold text-zinc-900" style={{ fontFamily: "'Chivo'" }}>Assumptions & Notes</h3></div>
      <div className="px-5 py-4"><ul className="space-y-2">
        {(data.assumptions || []).map((a) => <li key={a} className="flex items-start gap-2 text-[13px] text-zinc-600"><span className="text-zinc-300 shrink-0">&bull;</span>{a}</li>)}
      </ul></div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function MISReport({ companyContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [filters, setFilters] = useState({});
  const [exporting, setExporting] = useState(false);
  const [includeAudit, setIncludeAudit] = useState(true);

  const companyId = companyContext?.companyId;
  const branchId = companyContext?.branchId;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, include_audit: includeAudit };
      if (companyId) params.company_id = companyId;
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(`${API}/mis/report`, { params });
      setData(res.data);
      setError(null);
    } catch (err) { setError(err.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [filters, companyId, branchId, includeAudit]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      if (companyId) params.company_id = companyId;
      if (branchId) params.branch_id = branchId;
      const res = await axios.get(`${API}/mis/export/excel`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "MIS_Report.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("MIS Excel export failed:", err);
      toast.error("Failed to export MIS Excel");
    }
    finally { setExporting(false); }
  };

  // Build drill filters
  const drillFilters = { ...filters };
  if (companyId) drillFilters.company_id = companyId;
  if (branchId) drillFilters.branch_id = branchId;

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="mis-loading"><div className="spinner" /></div>;
  if (error) return <div className="text-center py-8 text-rose-600" data-testid="mis-error">{error}</div>;
  if (!data) return null;

  const renderTab = () => {
    switch (activeTab) {
      case "summary": return <SummaryTab data={data} companyContext={companyContext} filters={filters} onOpenAI={() => setActiveTab("ai")} />;
      case "ai": return <AICommentary companyContext={companyContext} filters={filters} />;
      case "charts": return <MISCharts data={data} />;
      case "pl": return <PLTab data={data} filters={drillFilters} />;
      case "monthly": return <MonthlyTab data={data} />;
      case "bs": return <BSTab data={data} filters={drillFilters} />;
      case "wc": return <WCTab data={data} />;
      case "ratios": return <RatiosTab data={data} />;
      case "redflags": return <RedFlagsTab data={data} />;
      case "comparison": return <MISComparison companyContext={companyContext} />;
      case "commentary": return <MISCommentary companyContext={companyContext} filters={filters} />;
      case "assumptions": return <AssumptionsTab data={data} />;
      default: return <SummaryTab data={data} companyContext={companyContext} filters={filters} onOpenAI={() => setActiveTab("ai")} />;
    }
  };

  return (
    <div data-testid="mis-report" className="max-w-[1400px]">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: "'Chivo'" }} data-testid="mis-title">
            MIS Dashboard
          </h1>
          <p className="text-[13px] text-zinc-400 mt-0.5 font-mono tracking-tight">
            {data.period.months?.length > 0 ? `${data.period.months[0]} — ${data.period.months[data.period.months.length - 1]}` : "All periods"}
          </p>
        </div>
        <MISSchedulePicker companyContext={companyContext} filters={filters} />
      </div>

      <DateFilter onFilterChange={setFilters} />

      {/* Cross-view tie-out safety check (visible only when >1 month) */}
      <MISTieOutPanel data={data} />

      {/* Pre-audit / Post-audit toggle */}
      <div className="flex items-center gap-2 mb-4" data-testid="mis-audit-toggle">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">View</span>
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" role="group">
          <button
            onClick={() => setIncludeAudit(true)}
            data-testid="mis-toggle-post-audit"
            className={`px-3 py-1 text-[11px] font-semibold tracking-tight transition ${
              includeAudit ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            Post-audit
          </button>
          <button
            onClick={() => setIncludeAudit(false)}
            data-testid="mis-toggle-pre-audit"
            className={`px-3 py-1 text-[11px] font-semibold tracking-tight transition border-l border-zinc-200 ${
              !includeAudit ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            Pre-audit
          </button>
        </div>
        <span className="text-[11px] text-zinc-400">
          {includeAudit ? "Includes Audit JVs" : "Excludes Audit JVs — source numbers only"}
        </span>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-0.5" data-testid="mis-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`mis-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-md whitespace-nowrap transition-all duration-100
                ${isActive
                  ? "bg-zinc-950 text-white shadow-sm"
                  : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60"}`}>
              <Icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          );
        })}
      </div>

      {renderTab()}
    </div>
  );
}
